import React from "react";

import { CrmDiffSyncChannel, LegacyMegaraxPricingRule, PricingRuleSheetRow } from "@megaron/crm-contracts";
import {
  DiffSyncDependentChannelLink,
  DiffSyncDependentChannelRefreshResult,
  DiffSyncPage,
  DiffSyncRecord,
} from "@megaron/diff-sync-webapp";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { Serializers } from "@megaron/serializers";

type PricingRuleDiffValue = Partial<PricingRuleSheetRow> & Record<string, unknown>;
type LegacyMegaraxPricingRuleDiffValue = Partial<LegacyMegaraxPricingRule> & Record<string, unknown>;

type Props<T extends Record<string, unknown>> = {
  title: string;
  channel: CrmDiffSyncChannel;
  directionLabels: {
    pull: string;
    push: string;
  };
  labels: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, (value: unknown) => React.ReactNode>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
};

export const PricingRulesSyncDiffPage = () => (
  <CrmPricingRulesSyncDiffPage<PricingRuleDiffValue>
    title="Synchronizacja Google Sheets - warunki handlowe"
    channel="sheetsPricingRule"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    labels={pricingRuleLabels}
    formatters={pricingRuleFormatters}
    alwaysVisibleColumns={["customerId", "customerName", "priceList"]}
  />
);

export const LegacyMegaraxPricingRulesSyncDiffPage = () => (
  <CrmPricingRulesSyncDiffPage<LegacyMegaraxPricingRuleDiffValue>
    title="Migracja warunków handlowych z legacy Megarax"
    channel="legacyMegaraxPricingRule"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do legacy Megarax",
    }}
    labels={legacyMegaraxPricingRuleLabels}
    formatters={legacyMegaraxPricingRuleFormatters}
    alwaysVisibleColumns={["customerId", "customerName", "priceList"]}
  />
);

const CrmPricingRulesSyncDiffPage = <T extends Record<string, unknown>>(props: Props<T>) => {
  const client = useClientManager("crm");
  const serviceClient = useServiceClient("crm");

  const pullQuery = client.diffQuery().useQuery({
    channel: props.channel,
    isPullable: true,
  });

  const pushQuery = client.diffQuery().useQuery({
    channel: props.channel,
    isPushable: true,
  });

  const allDiffsQuery = client.diffQuery().useQuery({
    channel: props.channel,
  });
  const channelQuery = client.diffChannelQuery().useQuery({
    channel: props.channel,
  });

  const refreshMutation = client.refreshDiffs().useMutation();
  const pullMutation = client.pullDiffs().useMutation();
  const pushMutation = client.pushDiffs().useMutation();

  const refetchDiffs = async () => {
    await Promise.all([pullQuery.refetch(), pushQuery.refetch(), allDiffsQuery.refetch()]);
  };

  const refresh = async () => {
    await refreshMutation.mutateAsync({ channel: props.channel });
    await refetchDiffs();
  };

  const pull = async (diffIds: string[]) => {
    try {
      await pullMutation.mutateAsync({ channel: props.channel, diffIds });
    } finally {
      await refetchDiffs();
    }
  };

  const push = async (diffIds: string[]) => {
    try {
      await pushMutation.mutateAsync({ channel: props.channel, diffIds });
    } finally {
      await refetchDiffs();
    }
  };

  const pullDependentChannels = React.useMemo(
    () =>
      channelQuery.data?.pullDependentChannels?.map((channel) => ({
        channel: channel.channel,
        name: channel.name,
        href: getMegaraxPath.uri("megarax://crm/diff-sync/{channel}", { channel: channel.channel }),
      })),
    [channelQuery.data?.pullDependentChannels],
  );

  const pushDependentChannels = React.useMemo(
    () =>
      channelQuery.data?.pushDependentChannels?.map((channel) => ({
        channel: channel.channel,
        name: channel.name,
        href: getMegaraxPath.uri("megarax://crm/diff-sync/{channel}", { channel: channel.channel }),
      })),
    [channelQuery.data?.pushDependentChannels],
  );

  const refreshDependentChannel = async (
    channel: DiffSyncDependentChannelLink,
  ): Promise<DiffSyncDependentChannelRefreshResult> => {
    try {
      const refreshResult = await serviceClient.refreshDiffs({ channel: channel.channel as CrmDiffSyncChannel });
      if (refreshResult.isFailure) throw new Error(formatClientError(refreshResult.error));

      const diffResult = await serviceClient.diffQuery({ channel: channel.channel as CrmDiffSyncChannel });
      if (diffResult.isFailure) throw new Error(formatClientError(diffResult.error));

      const diffs = diffResult.value as DiffSyncRecord<Record<string, unknown>>[];
      return {
        ...channel,
        changeCount: diffs.filter(isChangeDiff).length,
        errorCount: diffs.filter(isErrorDiff).length,
      };
    } catch (error) {
      return {
        ...channel,
        error: formatClientError(error),
      };
    }
  };

  return (
    <DiffSyncPage<T>
      title={props.title}
      pullableDiffs={pullQuery.data as DiffSyncRecord<T>[] | undefined}
      pushableDiffs={pushQuery.data as DiffSyncRecord<T>[] | undefined}
      errorDiffs={allDiffsQuery.data?.filter(isErrorDiff) as DiffSyncRecord<T>[] | undefined}
      directionLabels={props.directionLabels}
      labels={props.labels}
      formatters={props.formatters}
      alwaysVisibleColumns={props.alwaysVisibleColumns}
      remoteResourceUrl={channelQuery.data?.remoteResourceUrl}
      pullDependentChannels={pullDependentChannels}
      pushDependentChannels={pushDependentChannels}
      isLoading={
        pullQuery.isLoading ||
        pushQuery.isLoading ||
        allDiffsQuery.isLoading ||
        pullQuery.isFetching ||
        pushQuery.isFetching ||
        allDiffsQuery.isFetching
      }
      isRefreshing={refreshMutation.isLoading}
      isExecuting={pullMutation.isLoading || pushMutation.isLoading}
      onRefresh={refresh}
      onPull={pull}
      onPush={push}
      onRefreshDependentChannel={refreshDependentChannel}
    />
  );
};

const isErrorDiff = (diff: DiffSyncRecord<Record<string, unknown>>) => !!diff.error;
const isChangeDiff = (diff: DiffSyncRecord<Record<string, unknown>>) =>
  !diff.error && (diff.isPullable || diff.isPushable);

const formatClientError = (error: unknown) => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;

  return JSON.stringify(error) ?? String(error);
};

const pricingRuleLabels = {
  priceList: "Cennik",
  customerId: "Id klienta",
  customerName: "Klient",
  defaultDiscount: "Rabat domyślny",
  notes: "Notatki",
} satisfies Partial<Record<keyof PricingRuleSheetRow, string>>;

const pricingRuleFormatters = {
  defaultDiscount: (value) => (value === null || value === undefined ? "-" : value.toString()),
} satisfies Partial<Record<keyof PricingRuleSheetRow, (value: unknown) => React.ReactNode>>;

const legacyMegaraxPricingRuleLabels = {
  priceList: "Cennik",
  customerId: "Id klienta",
  customerName: "Klient",
  defaultDiscount: "Rabat domyślny",
  notes: "Notatki",
  modifiedAt: "Zmodyfikowano",
  modifiedBy: "Zmodyfikował",
} satisfies Partial<Record<keyof LegacyMegaraxPricingRule, string>>;

const legacyMegaraxPricingRuleFormatters = {
  defaultDiscount: (value) => (value === null || value === undefined ? "-" : value.toString()),
  modifiedAt: (value) => formatDateTimeValue(value),
} satisfies Partial<Record<keyof LegacyMegaraxPricingRule, (value: unknown) => React.ReactNode>>;

const formatDateTimeValue = (value: unknown) =>
  value instanceof Date ? Serializers.datetime.serialize(value) : value ? String(value) : "-";
